import {
  MUIMarkdown,
  MUIMarkdownProps,
} from '@modules/common/components/MUIMarkdown/MUIMarkdown';
import { Box, keyframes } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface MarkdownTypewriterProps extends MUIMarkdownProps {
  typingSpeed?: number;
  isLoading?: boolean;
}

const BLINK = keyframes`
  50% {
    opacity: 0.6;
  }
`;

export function MarkdownTypewriter({
  markdown,
  typingSpeed = 10,
  isLoading = false,
  components,
}: MarkdownTypewriterProps) {
  const prevIndexRef = useRef(0);
  const visibleMarkdownRef = useRef('');
  const [, setForceUpdate] = useState(0);

  useEffect(() => {
    if (prevIndexRef.current >= markdown.length) {
      return;
    }

    let index = prevIndexRef.current;

    const typewriterInterval = setInterval(() => {
      const possibleEndIndexForColDirective = (() => {
        const nextSpaceIndex = markdown.indexOf(' ', index);
        const columnDirectiveEndIndex = Math.max(
          markdown.indexOf('}', index),
          markdown.indexOf(']', index),
        );

        if (columnDirectiveEndIndex === -1) {
          return index;
        }

        if (nextSpaceIndex === -1) {
          return columnDirectiveEndIndex;
        }

        if (nextSpaceIndex < columnDirectiveEndIndex) {
          return columnDirectiveEndIndex;
        }

        return nextSpaceIndex;
      })();

      const nextIndex =
        markdown[index] === ':'
          ? possibleEndIndexForColDirective + 1
          : index + 1;

      visibleMarkdownRef.current += markdown.substring(index, nextIndex);
      index = nextIndex;

      setForceUpdate((prev) => prev + 1);

      if (index >= markdown.length) {
        prevIndexRef.current = 0;
        clearInterval(typewriterInterval);
      }
    }, typingSpeed);

    return () => {
      prevIndexRef.current = index;
      clearInterval(typewriterInterval);
    };
  }, [markdown, typingSpeed]);

  const isMarkdownLoading =
    visibleMarkdownRef.current !== markdown || isLoading;

  return (
    <Box
      sx={{
        '> :last-child::after': isMarkdownLoading
          ? {
              content: '""',
              display: 'inline-block',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: (theme) => theme.palette.text.primary,
              position: 'relative',
              left: '5px',

              animation: `${BLINK} 0.75s infinite`,
            }
          : {},
      }}
    >
      <MUIMarkdown
        markdown={visibleMarkdownRef.current}
        components={components}
      />
    </Box>
  );
}
